<template lang="pug">
  .addUnit
    //- Barra de navegación
    van-nav-bar(:title="$t('addUnit.title')")
      van-icon(name="menu" slot="left" @click="openSidenav")

    .main.padding-double-bottom
      //- Imágenes
      img.addUnit__img(v-if="init" src="@/assets/img/addUnitBluetooth.png")
      img.addUnit__img(v-if="!init && searchingDevices" src="@/assets/img/addUnitEmpty.png")
      img.addUnit__img(v-if="!init && !searchingDevices" src="@/assets/img/addUnitDevices.png")

      //- Mensaje inicial
      .addUnit__message.margin-double-bottom(v-if="init")
        | {{ $t('addUnit.init.checkNetwork') }}

      //- Barra de estado
      .addUnit__title(v-if="!init")
        span(v-if="!searchingDevices") {{ $t('addUnit.init.select') }}
        span(v-if="searchingDevices") {{ $t('addUnit.init.search') }}
        van-loading(v-if="searchingDevices" type="spinner")

      //- Listado de dispositivos encontrados
      van-cell-group(v-if="!init && webservers.length")
        van-cell.webserverRow(
          v-for="webserver in webservers"
          :key="webserver.id"
          :class="{'webserver__error': webserver.error}"
          :title="webserver.deviceName"
          :value="webserver.error"
          @click="showDeviceInfo(webserver.id, webserver.deviceName, webserver.error)"
          :is-link="!webserver.error")

      //- Mensaje cuando no se encuentran dispositivos
      .addUnit__message(v-if="!init && !searchingDevices && !webservers.length")
        .addUnit__message__title(v-if="!webservers.length")
          | {{ $t('addUnit.init.empty.titleEmpty') }}
        .addUnit__message__title(v-if="webservers.length")
          | {{ $t('addUnit.init.empty.titleNotFound') }}
        | {{ $t('addUnit.init.empty.message') }}

      //- Barra de estado
      .addUnit__title(v-if="!init && webserversOtherApps.length")
        span {{ $t('addUnit.unsuppportedUnits') }}

      van-cell-group(v-if="!init && webserversOtherApps.length")
        van-cell.webserverRow.unsopported(
          v-for="webserver in webserversOtherApps"
          :key="webserver.id"
          :class="{'webserver__error': webserver.error}"
          :title="webserver.deviceName"
          :value="webserver.error"
          :label="$t('addUnit.appRequired', { app: webserver.appName })"
          @click="openLinkMixin(webserver.appStore)"
          :is-link="!webserver.error")

      //- Mensaje cuando no se encuentran dispositivos
      .addUnit__message(v-if="!init && !searchingDevices && webservers.length")
        .addUnit__message__title(v-if="!webservers.length")
          | {{ $t('addUnit.init.empty.titleEmpty') }}
        .addUnit__message__title(v-if="webservers.length")
          | {{ $t('addUnit.init.empty.titleNotFound') }}
        | {{ $t('addUnit.init.empty.message') }}



    //- Botones
    .button-bottom-group(v-if="!searchingDevices")
      .button-bottom(@click="getDevicesBluetooth") {{ $t('addUnit.init.search') }}

    //- Device Info
    van-popup.van-popup--full(v-model="deviceInfoView" position="right" :overlay="false")
      DeviceInfo(
        v-if="deviceInfoView"
        :device="device"
        :pin="pin"
        @closeView="hideDeviceInfo")

    //- Device security PIN
    van-popup.addUnit__popup.addUnit__popup--security(v-model="deviceInfoPin")
      DevicePin(
        v-if="deviceInfoPin"
        :device="device"
        :pin="pin"
        @closeModal="hideDevicePIN"
        @success="successPin")
</template>

<script>
import Vue from 'vue';
import i18n from '@/locales/i18n';
import { mapGetters, mapActions } from 'vuex';
import BluetoothService from '@/services/bluetooth.service';
import AppsService from '@/services/apps.service';
import { validateWebserver } from '@/models/webserver.model';
import { getErrorId } from '@/services/error.service';
import { APPNAME } from '@/app.config';
import OpenLinkMixin from '@/mixins/OpenLink.mixin';

import DeviceInfo from '@/components/AddUnit/DeviceInfo.vue';
import DevicePin from '@/components/AddUnit/DevicePin.vue';

export default {
  components: {
    DeviceInfo,
    DevicePin,
  },
  mixins: [OpenLinkMixin],
  data() {
    return {
      init: true,
      searchingDevices: false,
      bluetoothError: null,
      isLoading: false,
      webservers: [],
      webserversOtherApps: [],
      deviceNamesArray: [],
      device: {},
      pin: '',
      deviceInfoView: false,
      deviceInfoPin: false,
      apps: {},
      appName: APPNAME,
    };
  },
  computed: {
    ...mapGetters({
      isDemo: 'globalModule/isDemo',
    }),
  },
  methods: {
    ...mapActions({
      openSidenav: 'globalModule/openSidenav',
    }),

    checkApp(deviceName) {
      const find = Object.keys(this.apps.prefixes).find(prefix => deviceName.startsWith(prefix));

      if (!find) return null;

      const appID = this.apps.prefixes[find];

      if (appID === APPNAME) return null;

      const appName = this.apps.apps[appID].name;
      const appStore = device.platform === 'iOS' ? this.apps.apps[appID].ios : this.apps.apps[appID].android; // eslint-disable-line

      return { appName, appStore };
    },

    /**
     * Get devices by Bluetooth and add to store
     */
    async getDevicesBluetooth() {
      try {
        // Demo user can't search devices by bluetooth
        if (this.isDemo) {
          this.$dialog.alert({
            title: i18n.t('errors.demoNotAllowed.title'),
            message: i18n.t('errors.demoNotAllowed.message'),
          });
          return;
        }

        // Inicializo valores
        this.init = false;
        this.searchingDevices = true;
        this.resetDevices();

        // Busco dispositivos mediante bluetooth
        // Le paso una función que se ejecutará cada vez que se encuente un dispositivo
        await BluetoothService.getDevicesBluetooth(this.apps.apps.global.prefix, this.addDevice);
      } catch (error) {
        // Obtengo el error
        const bluetoothError = getErrorId(error);

        const errorID = i18n.te(`errors.${bluetoothError}.title`) ? bluetoothError : 'default';

        this.$dialog.alert({
          title: i18n.t(`errors.${errorID}.title`),
          message: i18n.t(`errors.${errorID}.message`),
        });

        this.resetDevices();
      } finally {
        this.searchingDevices = false;
      }
    },

    /**
     * Añade un dispositivo a la lista de dispositivos encontrados
     */
    addDevice(device) {
      const appData = this.checkApp(device.deviceName.toUpperCase());

      if (appData) {
        device.appName = appData.appName;
        device.appStore = appData.appStore;
        const found = this.deviceNamesArray.some(() =>
          this.deviceNamesArray.includes(device.deviceName)
        );
        if (!found) {
          this.deviceNamesArray.push(device.deviceName);
          this.webserversOtherApps.push(device);
        }
        return;
      }

      this.webservers.push(device);
      this.webservers.sort((device1, device2) => {
        if (device1.rssi < device2.rssi) return 1;
        return -1;
      });
    },

    /**
     * Limpia el listado de webservers
     */
    resetDevices() {
      this.webservers.forEach(webserver => this.disconnectDevice(webserver.id));
      this.webservers.length = 0;
    },

    /*
     * Show Popup with Device Info
     */
    async showDeviceInfo(webserverID, webserverName, webserverError) {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading || webserverError) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.bluetoothError = false;
        console.log('showDeviceInfo');
        // Obtengo los datos del dispositivo
        const webserver = await BluetoothService.getDeviceInfo(webserverID);
        console.log('webserver: ', webserver);
        // Obtengo los datos del calor auxiliar y la entrada digital del dispositivo
        const response = await BluetoothService.getDeviceParams(webserverID, [
          'auxheat_enable',
          'auxheat_ton',
          'auxheat_toff',
          'auxheat_tdelay',
          'auxheat_lockoutenable',
          'auxheat_lockouttemp',
          'auxheat_extheatsource',
          'auxheat_emergencyheat',
          't1t2_enable',
          't1t2_nc',
          't1t2_ondelay',
          't1t2_offdelay',
          'rs485_mode',
          'rs485_availablemodes',
          'ecobeesb',
        ]);
        console.log('response: ', response);
        if (response && response.params) {
          webserver.params = response.params;
        }
        console.log('webserver after params: ', webserver);
        if (webserver.general.ble_version >= 2) {
          // Pedimos y asignamos parámetros ecobee SB si tiene ese servicio y está activado
          if (webserver.params && webserver.params.ecobeesb === 1) {
            const ecobeeData = await BluetoothService.getDeviceParams(webserverID, [
              'ecobeesbclientid',
              'ecobeesbclientsecret',
              'ecobeesbthermostatid',
              'ecobeesbstatus',
            ]);
            webserver.params.ecobeesbclientid = ecobeeData.params.ecobeesbclientid;
            webserver.params.ecobeesbclientsecret = ecobeeData.params.ecobeesbclientsecret;
            webserver.params.ecobeesbthermostatid = ecobeeData.params.ecobeesbthermostatid;
            webserver.params.ecobeesbstatus = ecobeeData.params.ecobeesbstatus;
          }

          const resp = await BluetoothService.getWsServices(webserverID);
          if (resp && resp.services) {
            webserver.services = resp.services;
            // Pedimos y asignamos parámetros Pelican si tiene ese servicio y está activado
            if (webserver.services && webserver.services.pelican === 1) {
              const pelicanData = await BluetoothService.getDeviceParams(webserverID, [
                'pelicansite',
                'pelicanemail',
                'pelicanpass',
                'pelicanserial',
                'pelicanstatus',
              ]);
              webserver.params.pelicansite = pelicanData.params.pelicansite;
              webserver.params.pelicanemail = pelicanData.params.pelicanemail;
              webserver.params.pelicanpass = pelicanData.params.pelicanpass;
              webserver.params.pelicanserial = pelicanData.params.pelicanserial;
              webserver.params.pelicanstatus = pelicanData.params.pelicanstatus;
            }
          }
          console.log('webserver after services: ', webserver);
        }

        this.device = await validateWebserver(webserverID, webserverName, webserver);

        this.$toast.clear();

        if (this.device.isUserAllow) {
          this.deviceInfoView = true;
        } else {
          this.deviceInfoPin = true;
        }
      } catch (error) {
        // Obtengo el error
        const bluetoothError = getErrorId(error);
        const errorID = i18n.te(`errors.${bluetoothError}.title`) ? bluetoothError : 'default';

        this.webservers.map(webserver => {
          if (webserver.id === webserverID) {
            Vue.set(webserver, 'error', i18n.t('addUnit.info.deviceErrorConnect'));
          }
          return webserver;
        });

        this.$toast.clear();

        this.$dialog
          .alert({
            title: i18n.t(`errors.${errorID}.title`),
            message: i18n.t(`errors.${errorID}.message`),
          })
          .then(() => {
            this.disconnectDevice(webserverID);
          });
      } finally {
        this.isLoading = false;
      }
    },

    /*
     * Hide Popup with Device Info
     */
    hideDeviceInfo() {
      this.disconnectDevice(this.device.id);
      this.device = '';
      this.deviceInfoView = false;
      // this.resetDevices();
      // this.init = true;
    },

    /*
     * Disconnect bluetooth device
     */
    disconnectDevice(webserverID) {
      try {
        BluetoothService.disconnectDevice(webserverID);
      } catch (error) {
        // Obtengo el error
        const bluetoothError = getErrorId(error);

        const errorID = i18n.te(`errors.${bluetoothError}.title`) ? bluetoothError : 'default';

        this.$dialog
          .alert({
            title: i18n.t(`errors.${errorID}.title`),
            message: i18n.t(`errors.${errorID}.message`),
          })
          .then(() => {});
      }
    },

    /*
     * Device Connection Lost
     */
    // deviceConnectionLost(deviceID) {
    //   if (this.deviceID === deviceID) {
    //     this.device = '';
    //     this.deviceInfoView = false;

    //     this.$dialog
    //       .alert({
    //         title: i18n.t('new.addUnit.deviceConnectionLost.title'),
    //         message: i18n.t('new.addUnit.deviceConnectionLost.message'),
    //       })
    //       .then(() => {
    //         this.resetDevices();
    //         this.init = true;
    //       });
    //   }
    // },

    /*
     * Hide Popup with Device Password Input PIN
     */
    hideDevicePIN() {
      this.pin = '';
      this.device = '';
      this.deviceInfoPin = false;
    },

    /*
     * Success Pin event
     */
    successPin(pin) {
      this.pin = pin;
      this.deviceInfoPin = false;
      this.deviceInfoView = true;
    },
  },

  async created() {
    this.apps = await AppsService.getAppsPrefix();
  },
};
</script>
